import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { uuidv7 } from 'uuidv7';
import { api, APIError } from '../api/client';
import { clearAuthSession, getUserId } from '../utils/auth';
import { getSavedJobs } from '../utils/savedJobs';
import { useAuthStore } from '../stores/useAuthStore';

export const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
    { code: 'it', name: 'Italian' },
    { code: 'cz', name: 'Czech' },
    { code: 'pl', name: 'Polish' },
    { code: 'sk', name: 'Slovak' },
    { code: 'uk', name: 'Ukrainian' },
];

const FORMAT_QUESTION_LIMITS = {
    quick: 5,
    standard: 11,
    full: 20,
};

const resolveQuestionCountByFormat = (interviewFormat) => {
    const key = (interviewFormat || '').toString().trim().toLowerCase();
    return FORMAT_QUESTION_LIMITS[key] || 20;
};

const LANGUAGE_ALIASES = {
    en: 'en',
    english: 'en',
    ru: 'ru',
    russian: 'ru',
    es: 'es',
    spanish: 'es',
    de: 'de',
    german: 'de',
    fr: 'fr',
    french: 'fr',
    it: 'it',
    italian: 'it',
    cs: 'cz',
    cz: 'cz',
    czech: 'cz',
    pl: 'pl',
    polish: 'pl',
    sk: 'sk',
    slovak: 'sk',
    ua: 'uk',
    uk: 'uk',
    ukrainian: 'uk',
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const sleep = (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms));

const normalizeLanguageCode = (value) => {
    const key = String(value || '').trim().toLowerCase();
    return LANGUAGE_ALIASES[key] || null;
};

const resolveLanguageOption = (value) => {
    const normalizedCode = normalizeLanguageCode(value);
    return LANGUAGES.find((item) => item.code === normalizedCode) || null;
};

const extractFirstJsonObject = (raw) => {
    if (typeof raw !== 'string' || !raw) {
        return null;
    }

    for (let start = 0; start < raw.length; start += 1) {
        if (raw[start] !== '{') continue;

        let depth = 0;
        let inString = false;
        let escaped = false;

        for (let end = start; end < raw.length; end += 1) {
            const char = raw[end];

            if (inString) {
                if (escaped) {
                    escaped = false;
                    continue;
                }
                if (char === '\\') {
                    escaped = true;
                    continue;
                }
                if (char === '"') {
                    inString = false;
                }
                continue;
            }

            if (char === '"') {
                inString = true;
                continue;
            }
            if (char === '{') {
                depth += 1;
                continue;
            }
            if (char === '}') {
                depth -= 1;
                if (depth === 0) {
                    const candidate = raw.slice(start, end + 1);
                    try {
                        const parsed = JSON.parse(candidate);
                        if (isPlainObject(parsed)) {
                            return { parsed, start, end: end + 1 };
                        }
                    } catch {
                        // Continue scanning for next possible JSON object.
                    }
                    break;
                }
            }
        }
    }

    return null;
};

const normalizeFeedbackPayload = (rawSummary, barsFromApi = null) => {
    const original = typeof rawSummary === 'string' ? rawSummary : String(rawSummary || '');
    let summary = original.trim();
    let bars = isPlainObject(barsFromApi) ? barsFromApi : null;

    const extracted = extractFirstJsonObject(original);
    if (extracted && isPlainObject(extracted.parsed)) {
        const parsedFeedback = extracted.parsed.feedback;
        if (typeof parsedFeedback === 'string' && parsedFeedback.trim()) {
            summary = parsedFeedback.trim();
        } else if (extracted.start > 0) {
            summary = original.slice(0, extracted.start).trim();
        }

        if (!bars && isPlainObject(extracted.parsed.bars_evaluation)) {
            bars = extracted.parsed.bars_evaluation;
        }
    }

    return { summary, bars };
};

export function useChatInterview() {
    const navigate = useNavigate();
    const { jobId } = useParams();
    const location = useLocation();
    const persistedProfileLanguage = useAuthStore((state) => state.user?.language);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const initInFlightRef = useRef(false);
    const initDoneForJobRef = useRef(null);
    const questionRequestSeqRef = useRef(0);

    // State
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isBootstrapping, setIsBootstrapping] = useState(true);
    const [isFinished, setIsFinished] = useState(false);
    const [interviewId, setInterviewId] = useState(null);
    const [interviewFormat, setInterviewFormat] = useState('standard');
    const [questionCount, setQuestionCount] = useState(20);
    const [questionStep, setQuestionStep] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [error, setError] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [vacancyDescription, setVacancyDescription] = useState('');
    const [activeTab, setActiveTab] = useState('chat');
    const [vacancyIdForDetails, setVacancyIdForDetails] = useState(null);

    // New State for Welcome Flow
    const [isStarted, setIsStarted] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0]);
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [showLangMenu, setShowLangMenu] = useState(false);
    const [hasManualLanguageSelection, setHasManualLanguageSelection] = useState(false);

    // Subscription Limit Modal State
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [limitErrorMessage, setLimitErrorMessage] = useState('');
    const [isLimitReached, setIsLimitReached] = useState(false);
    const [aiProcessingStatus, setAiProcessingStatus] = useState(null);

    const storageKey = `interviewStateByJob`;
    const redirectPath = jobId ? `/chat/${jobId}` : '/chat';

    const handleUnauthorized = useCallback(() => {
        clearAuthSession();
        navigate('/', {
            replace: true,
            state: { openAuth: true, action: 'search', redirectTo: redirectPath },
        });
    }, [navigate, redirectPath]);

    const showAiProcessing = useCallback((status) => {
        setAiProcessingStatus(status);
    }, []);

    const hideAiProcessing = useCallback(() => {
        setAiProcessingStatus(null);
    }, []);

    const buildAiProcessingStatus = useCallback((config) => ({
        state: 'active',
        ...config,
    }), []);

    const completeAiProcessing = useCallback(async (status, overrides = {}, delayMs = 900) => {
        const steps = Array.isArray(status?.steps)
            ? status.steps.map((step) => ({ ...step, status: 'completed' }))
            : [];

        setAiProcessingStatus({
            ...status,
            ...overrides,
            state: 'success',
            progress: 100,
            steps,
        });
        await sleep(delayMs);
        setAiProcessingStatus(null);
    }, []);

    // Reset textarea height when input is cleared
    useEffect(() => {
        if (input === '' && textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, [input]);

    useEffect(() => {
        if (hasManualLanguageSelection || interviewId) return;
        const preferred = resolveLanguageOption(persistedProfileLanguage);
        if (preferred && preferred.code !== selectedLanguage.code) {
            setSelectedLanguage(preferred);
        }
    }, [hasManualLanguageSelection, interviewId, persistedProfileLanguage, selectedLanguage.code]);

    const buildTimestamp = () =>
        new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });

    const createMessage = (role, content, variant, extra = {}) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role,
        content,
        variant,
        timestamp: buildTimestamp(),
        ...extra,
    });

    const appendMessage = (message) => {
        setMessages((prev) => [...prev, message]);
    };

    const appendFinalReportMessage = (content, structuredReport = null) => {
        const normalizedContent = typeof content === 'string' ? content.trim() : String(content || '').trim();
        if (!normalizedContent) return;

        setMessages((prev) => {
            const existingIndex = prev.findIndex((message) => message.variant === 'final');
            const nextMessage = createMessage('assistant', normalizedContent, 'final', {
                structuredReport: isPlainObject(structuredReport) ? structuredReport : null,
            });

            if (existingIndex === -1) {
                return [...prev, nextMessage];
            }

            const next = [...prev];
            next[existingIndex] = {
                ...next[existingIndex],
                content: normalizedContent,
                structuredReport: isPlainObject(structuredReport) ? structuredReport : next[existingIndex].structuredReport ?? null,
            };
            return next;
        });
    };

    const formatApiError = (data, fallback) => {
        if (!data) return fallback;
        if (typeof data === 'string') return data;
        if (typeof data.detail === 'string') return data.detail;
        if (typeof data.message === 'string') return data.message;
        if (Array.isArray(data.detail)) {
            const parts = data.detail
                .map((item) => {
                    if (!item) return null;
                    if (typeof item === 'string') return item;
                    if (typeof item.msg === 'string') return item.msg;
                    if (typeof item.message === 'string') return item.message;
                    return null;
                })
                .filter(Boolean);
            return parts.length ? parts.join('; ') : fallback;
        }
        return fallback;
    };

    const shouldRetryTransientInterviewError = (err) => {
        if (!(err instanceof APIError)) return false;
        if (err.status !== 502 && err.status !== 503) return false;
        const detail = formatApiError(err.data, '').toLowerCase();
        return (
            !detail ||
            detail.includes('ai model is busy') ||
            detail.includes('retry') ||
            detail.includes('timed out') ||
            detail.includes('provider error')
        );
    };

    const runInterviewRequestWithRetry = async (requestFactory, options = {}) => {
        const {
            attempts = 3,
            baseDelayMs = 1200,
        } = options;

        let lastError = null;
        for (let attempt = 1; attempt <= attempts; attempt += 1) {
            try {
                return await requestFactory();
            } catch (err) {
                lastError = err;
                if (!shouldRetryTransientInterviewError(err) || attempt >= attempts) {
                    throw err;
                }
                await sleep(baseDelayMs * attempt);
            }
        }

        throw lastError;
    };

    const isUuid = (value) =>
        typeof value === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

    const extractCompanyName = (job) => {
        if (!job || typeof job !== 'object') return '';
        if (typeof job.company === 'string' && job.company.trim()) return job.company.trim();
        if (typeof job.company_name === 'string' && job.company_name.trim()) return job.company_name.trim();
        if (typeof job.employer_name === 'string' && job.employer_name.trim()) return job.employer_name.trim();
        return '';
    };

    const navigationState = location.state;
    const navigationVacancyId = typeof navigationState?.vacancyId === 'string' ? navigationState.vacancyId : null;
    const isManualVacancyContext =
        navigationState?.source === 'manual_vacancy' || (isUuid(navigationVacancyId) && navigationVacancyId === jobId);

    const loadExistingInterview = async (interviewId) => {
        try {
            console.log('[Chat] Attempting to load existing interview:', interviewId);

            const interview = await api.get(`/api/interviews/${interviewId}`);
            console.log('[Chat] Successfully loaded existing interview:', interview);
            return interview;
        } catch (err) {
            if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
                handleUnauthorized();
                return null;
            }
            console.error('[Chat] Error loading existing interview:', err);
            return null;
        }
    };

    const restoreInterviewFromDB = (interviewData) => {
        console.log('[Chat] Restoring interview from database:', interviewData);

        // Set basic interview state
        setInterviewId(interviewData.interview_id);
        setQuestionStep(interviewData.step);
        const restoredFormat = (interviewData.interview_format || 'standard').toLowerCase();
        setInterviewFormat(restoredFormat);
        const restoredQuestionCount =
            (typeof interviewData.questions_count === 'number' && Number.isFinite(interviewData.questions_count))
                ? interviewData.questions_count
                : resolveQuestionCountByFormat(restoredFormat);
        setQuestionCount(restoredQuestionCount);

        // Set job title if not already set from navigation state
        const title = interviewData.vacancy_title || 'Interview';
        setJobTitle((prevTitle) => prevTitle || title);
        console.log('[Chat] Set jobTitle:', title);
        if (typeof interviewData.vacancy_company === 'string' && interviewData.vacancy_company.trim()) {
            setCompanyName((prev) => prev || interviewData.vacancy_company.trim());
        }

        // Store vacancy_id for JobDetailsContent if not already set
        if (interviewData.vacancy_id) {
            setVacancyIdForDetails((prevId) => prevId || interviewData.vacancy_id);
            console.log('[Chat] Set vacancy_id for details:', interviewData.vacancy_id);
        } else {
            console.warn('[Chat] No vacancy_id in interview data!');
        }

        if (interviewData.vacancy_description) {
            setVacancyDescription(interviewData.vacancy_description);
            console.log('[Chat] Set vacancyDescription from API, length:', interviewData.vacancy_description.length);
        }

        // Set language
        const foundLang = LANGUAGES.find(l => l.code === interviewData.language);
        if (foundLang) {
            setSelectedLanguage(foundLang);
        }

        // Restore messages from chat_history
        const restoredMessages = [];
        const history = interviewData.chat_history || [];
        let restoredStructuredReport = null;

        history.forEach((item) => {
            if (item?.type === 'report') {
                if (item.structured_report && !restoredStructuredReport) {
                    restoredStructuredReport = item.structured_report;
                }
                return;
            }
            // Add question
            if (item.q) {
                restoredMessages.push(createMessage('assistant', item.q, 'question', { questionStep: item.num ?? 0 }));
            }
            // Add user answer
            if (item.a) {
                restoredMessages.push(createMessage('user', item.a));
            }
            // Add feedback
            if (item.f) {
                const normalizedFeedback = normalizeFeedbackPayload(item.f, item.bars);
                restoredMessages.push(
                    createMessage(
                        'assistant',
                        normalizedFeedback.summary || item.f,
                        'summary',
                        { bars: normalizedFeedback.bars },
                    ),
                );
            }
        });

        // Check interview status
        if (interviewData.interview_status === 2) {
            // Completed
            console.log('[Chat] Interview is completed');
            setIsFinished(true);
            if (interviewData.summary) {
                restoredMessages.push(
                    createMessage('assistant', interviewData.summary, 'final', {
                        structuredReport: restoredStructuredReport,
                    })
                );
            }
            setIsStarted(true);
        } else {
            // In progress - fetch next question
            console.log('[Chat] Interview in progress, fetching next question');
            setIsStarted(true);
            fetchQuestion(interviewData.interview_id);
        }

        setMessages(restoredMessages);
        console.log('[Chat] Restored', restoredMessages.length, 'messages');
        setIsBootstrapping(false);
    };

    const findVacancyId = () => {
        // If caller passes explicit vacancy_id via navigation state, prefer it over any localStorage mappings.
        if (isUuid(navigationVacancyId)) {
            return navigationVacancyId;
        }

        try {
            const storedVacancies = JSON.parse(localStorage.getItem('vacancyIdsByJob') || '{}');
            const storedVacancyId = storedVacancies?.[jobId];
            if (isUuid(storedVacancyId)) {
                return storedVacancyId;
            }
        } catch (error) {
            console.error('[Chat] Failed to read vacancyIdsByJob:', error);
        }

        const jobsData = localStorage.getItem('currentJobs');
        if (!jobsData) {
            return isUuid(jobId) ? jobId : uuidv7();
        }

        try {
            const jobs = JSON.parse(jobsData);
            const foundJob = jobs.find((job) => job.id === jobId);
            if (isUuid(foundJob?.job_id)) {
                return foundJob.job_id;
            }
            if (isUuid(jobId)) {
                return jobId;
            }
            return uuidv7();
        } catch (parseError) {
            console.error('[Chat] Failed to parse currentJobs:', parseError);
            return isUuid(jobId) ? jobId : uuidv7();
        }
    };

    const loadInterviewState = (jobKey) => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed?.[jobKey] ?? null;
        } catch (error) {
            console.error('[Chat] Failed to read interview state:', error);
            return null;
        }
    };

    const saveInterviewState = (jobKey, state) => {
        try {
            const raw = localStorage.getItem(storageKey);
            const parsed = raw ? JSON.parse(raw) : {};
            parsed[jobKey] = state;
            localStorage.setItem(storageKey, JSON.stringify(parsed));
        } catch (error) {
            console.error('[Chat] Failed to save interview state:', error);
        }
    };

    const fetchQuestion = async (currentInterviewId, statusOverride = null) => {
        if (!currentInterviewId) {
            setError('Interview is not initialized. Please try again or upgrade your plan.');
            setIsLoading(false);
            setIsBootstrapping(false);
            return;
        }
        const requestSeq = ++questionRequestSeqRef.current;
        try {
            setIsLoading(true);
            setError('');
            const popupStatus = statusOverride || buildAiProcessingStatus({
                phase: 'question',
                title: questionStep > 0 ? 'Preparing next question' : 'Preparing first question',
                description: 'The interview agent is reading your context and generating the next prompt.',
                label: 'Question generation',
                steps: [
                    { id: 'context', label: 'Read interview context', status: 'active' },
                    { id: 'generate', label: 'Generate next question', status: 'pending' },
                    { id: 'deliver', label: 'Deliver question to chat', status: 'pending' },
                ],
            });
            showAiProcessing(popupStatus);

            const data = await runInterviewRequestWithRetry(
                () => api.get(`/api/interviews/${currentInterviewId}/question`),
            );

            if (requestSeq !== questionRequestSeqRef.current) {
                return; // stale response, ignore
            }
            setQuestionStep(data.question_step ?? 0);
            setCurrentQuestion({
                text: data.question_text ?? '',
                step: data.step ?? 0,
                questionStep: data.question_step ?? 0,
            });
            setMessages((prev) => {
                const existing = prev.some(
                    (message) =>
                        message.variant === 'question' &&
                        Number(message.questionStep) === Number(data.question_step),
                );
                if (existing) return prev;
                return [...prev, createMessage('assistant', data.question_text, 'question', { questionStep: data.question_step ?? 0 })];
            });

            await completeAiProcessing(
                popupStatus,
                {
                    title: data.question_step > 1 ? 'Next question is ready' : 'Interview is ready',
                    description: 'The next prompt has been added to the conversation.',
                },
                800,
            );
        } catch (err) {
            if (err instanceof APIError) {
                if (err.status === 401 || err.status === 403) {
                    hideAiProcessing();
                    handleUnauthorized();
                    return;
                }
                if (err.status === 402) {
                    // In this endpoint 402 can mean the interview question cap is reached.
                    // That should trigger finalization, not a tariff/paywall popup.
                    const detailText = formatApiError(err.data, '').toLowerCase();
                    if (detailText.includes('question limit reached')) {
                        await handleFinishNow();
                        return;
                    }
                    // Fallback: keep paywall for actual plan-gating responses.
                    hideAiProcessing();
                    setLimitErrorMessage(formatApiError(err.data, 'Question limit reached. Please upgrade your plan.'));
                    setShowLimitModal(true);
                    return;
                }
                if (err.status === 404) {
                    // Could be finished or really not found
                    if (err.data?.detail === "Interview finished") {
                        hideAiProcessing();
                        setIsFinished(true);
                        return;
                    }
                    hideAiProcessing();
                    setError('No questions available.');
                    setIsFinished(true);
                    return;
                }
                hideAiProcessing();
                setError(formatApiError(err.data, 'Failed to fetch the question. Please try again later.'));
                return;
            }
            console.error('[Chat] Question fetch error:', err);
            setError('Error while fetching the question.');
            hideAiProcessing();
        } finally {
            setIsLoading(false);
            setIsBootstrapping(false);
        }
    };

    const updateWelcomeMessage = async (langCode, title, format = 'standard') => {
        const questionsCount = resolveQuestionCountByFormat(format);
        try {
            // Default message for immediate feedback
            const defaultMsg = `Hi! I'm your AI Interview agent, here to help you ace your next interview for the ${title} position. We'll go through a ${questionsCount}-question mock session. Once we're done, I'll provide detailed tips on your answers, highlight skills to improve, and give you an AI-powered assessment of your job fit. Shall we start?`;

            if (langCode === 'en') {
                setWelcomeMessage(defaultMsg);
                return;
            }

            setIsLoading(true);
            const data = await api.post('/api/interviews/welcome_message', {
                language: langCode,
                job_title: title,
                interview_format: format,
            });
            setWelcomeMessage(data.message);
        } catch (err) {
            console.error("Welcome message translation failed", err);
            // Fallback to default on error
            const defaultMsg = `Hi! I'm your AI Interview agent, here to help you ace your next interview for the ${title} position. We'll go through a ${questionsCount}-question mock session. Once we're done, I'll provide detailed tips on your answers, highlight skills to improve, and give you an AI-powered assessment of your job fit. Shall we start?`;
            setWelcomeMessage(defaultMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLanguageChange = (lang) => {
        setHasManualLanguageSelection(true);
        setSelectedLanguage(lang);
        setShowLangMenu(false);
        updateWelcomeMessage(lang.code, jobTitle, interviewFormat);
    };

    const initInterview = async () => {
        try {
            // Prevent duplicate init calls (effects can fire twice while title is resolving).
            if (!jobId) return;
            if (initInFlightRef.current) return;
            if (initDoneForJobRef.current === jobId && interviewId) return;

            initInFlightRef.current = true;
            setIsBootstrapping(true);
            setError('');
            setIsLimitReached(false);
            const userId = getUserId();
            if (!userId) {
                setError('Session expired. Please log in again.');
                setIsBootstrapping(false);
                handleUnauthorized();
                return;
            }

            const vacancyId = findVacancyId();
            let resolvedLanguage = selectedLanguage;

            if (!hasManualLanguageSelection) {
                try {
                    const profile = await api.get('/api/auth/profile');
                    const preferred = resolveLanguageOption(profile?.language);
                    if (preferred) {
                        resolvedLanguage = preferred;
                        setSelectedLanguage(preferred);
                    }
                } catch (profileError) {
                    console.warn('[Chat] Failed to load preferred profile language:', profileError);
                }
            }

            // Look for cached state first
            const cached = loadInterviewState(jobId);
            if (cached?.interviewId && !cached.isFinished) {
                setInterviewId(cached.interviewId);
                const cachedFormat = (cached.interviewFormat || 'standard').toLowerCase();
                setInterviewFormat(cachedFormat);
                setQuestionCount(
                    typeof cached.questionCount === 'number' && Number.isFinite(cached.questionCount)
                        ? cached.questionCount
                        : resolveQuestionCountByFormat(cachedFormat)
                );
                setQuestionStep(cached.questionStep);
                setMessages(cached.messages || []);
                setIsStarted(true); // User already started previously
                setIsBootstrapping(false);

                if (!cached.currentQuestion) {
                    fetchQuestion(cached.interviewId, {
                        phase: 'bootstrapping',
                        title: 'Restoring your interview',
                        description: 'We are loading your saved session and requesting the active question.',
                        label: 'Session restore',
                    });
                } else {
                    setCurrentQuestion(cached.currentQuestion);
                }
                setIsFinished(Boolean(cached.isFinished));
                return;
            }

            // Try to load existing interview from database if jobId is a UUID
            // When navigating from manual vacancy details, jobId is a vacancy_id, not an interview_id.
            if (isUuid(jobId) && !isManualVacancyContext) {
                console.log('[Chat] jobId is UUID, checking if it\'s an existing interview...');
                const existingInterview = await loadExistingInterview(jobId);
                if (existingInterview) {
                    // Found existing interview in database - restore it
                    restoreInterviewFromDB(existingInterview);
                    return;
                }
                console.log('[Chat] Not an existing interview ID, treating as vacancy/job ID');
            }

            const initPayload = {
                vacancy_id: vacancyId,
                vacancy_title: jobTitle || null,
                language: resolvedLanguage.code,
                interview_format: interviewFormat,
            };

            try {
                const data = await api.post('/api/interviews/init', initPayload);
                if (!data?.interview_id) {
                    const detailText = formatApiError(data, '');
                    const normalizedDetail = detailText.toLowerCase();

                    if (normalizedDetail.includes('vacancy parsing started') || normalizedDetail.includes('vacancy parsing in progress')) {
                        setError('Vacancy details are still being prepared. Please wait a few seconds and try again.');
                    } else {
                        setError(detailText || 'Interview is not ready yet. Please try again in a few seconds.');
                    }
                    setIsBootstrapping(false);
                    return;
                }
                setInterviewId(data.interview_id);
                if (typeof data.questions_count === 'number' && Number.isFinite(data.questions_count)) {
                    setQuestionCount(data.questions_count);
                } else {
                    setQuestionCount(resolveQuestionCountByFormat(interviewFormat));
                }
                initDoneForJobRef.current = jobId;
                setIsStarted(true);
                if (messages.length === 0) {
                    await fetchQuestion(data.interview_id, {
                        phase: 'bootstrapping',
                        title: 'Starting interview session',
                        description: 'We are preparing the first question using the vacancy and your profile context.',
                        label: 'Session setup',
                    });
                }
                setIsBootstrapping(false);
            } catch (err) {
                if (err instanceof APIError) {
                    if (err.status === 409) {
                        const detailText = formatApiError(err.data, '');
                        const normalizedDetail = detailText.toLowerCase();

                        if (err.data?.interview_id) {
                            console.log('[Chat] Recovered existing interview ID from 409:', err.data.interview_id);
                            setInterviewId(err.data.interview_id);
                            setIsStarted(true);
                            if (messages.length === 0) {
                                await fetchQuestion(err.data.interview_id, {
                                    phase: 'bootstrapping',
                                    title: 'Restoring interview session',
                                    description: 'An active session was found. We are loading the current question.',
                                    label: 'Session restore',
                                });
                            }
                            setIsBootstrapping(false);
                            return;
                        }

                        if (normalizedDetail.includes('vacancy parsing in progress')) {
                            setError('Vacancy details are still being prepared. Please wait a few seconds and try again.');
                            setIsBootstrapping(false);
                            return;
                        }

                        setError(detailText || 'This interview cannot be started right now. Please try again in a few seconds.');
                        setIsBootstrapping(false);
                        return;
                    }

                    if (err.status === 401 || err.status === 403) {
                        handleUnauthorized();
                        return;
                    }

                    if (err.status === 402) {
                        // Payment Required - Show subscription limit modal
                        setLimitErrorMessage(formatApiError(err.data, 'Monthly interview limit reached. Please upgrade your plan.'));
                        setIsLimitReached(true);
                        setShowLimitModal(true);
                        setIsBootstrapping(false);
                        return;
                    }

                    setError(formatApiError(err.data, 'Failed to start the interview.'));
                    setIsBootstrapping(false);
                    return;
                }
                throw err; // Re-throw non-API errors
            }

        } catch (err) {
            console.error('[Chat] Init error:', err);
            setError('Error starting the interview.');
            setIsBootstrapping(false);
        } finally {
            initInFlightRef.current = false;
        }
    };

    useEffect(() => {
        if (!jobId) {
            setError('Job not found.');
            setIsBootstrapping(false);
            return;
        }

        // Check if we have data from navigation state (from profile)
        const navigationState = location.state;
        if (navigationState?.vacancyTitle) {
            console.log('[Chat] Using vacancy data from navigation state:', navigationState);
            setJobTitle(navigationState.vacancyTitle);
            if (typeof navigationState.vacancyCompany === 'string') {
                setCompanyName(navigationState.vacancyCompany);
            }
            if (navigationState.vacancyId) {
                setVacancyIdForDetails(navigationState.vacancyId);
            }
            return; // Skip localStorage lookup
        }

        // Get job title from local storage first
        try {
            const jobsData = localStorage.getItem('currentJobs');
            if (jobsData) {
                const jobs = JSON.parse(jobsData);
                const foundJob = jobs.find((job) => job.id === jobId);
                if (foundJob?.title) {
                    setJobTitle(foundJob.title);
                    setCompanyName(extractCompanyName(foundJob));
                    // When starting new interview, jobId is the job/vacancy ID
                    setVacancyIdForDetails(jobId);
                }
            }

            // Also check savedJobs if not found in currentJobs
            if (!jobTitle) {
                const savedJobs = getSavedJobs();
                const foundJob = savedJobs.find((job) => String(job.id) === String(jobId));
                if (foundJob?.title) {
                    setJobTitle(foundJob.title);
                    setCompanyName(extractCompanyName(foundJob));
                    setVacancyIdForDetails(jobId);
                }
            }
        } catch (err) {
            console.warn('[Chat] Failed to read job title:', err);
        }
    }, [jobId, location.state]);

    // Need to run init after we have jobTitle set basically
    useEffect(() => {
        if (jobTitle) {
            initInterview();
        } else if (jobId) {
            // Fallback: allow init even without resolved title (e.g. /chat/{interview_id} after /chat-setting).
            initInterview();
        }
    }, [jobId, jobTitle]);


    useEffect(() => {
        if (!jobId || !interviewId) return;
        saveInterviewState(jobId, {
            interviewId,
            interviewFormat,
            questionCount,
            questionStep,
            currentQuestion,
            messages,
            isFinished,
            updatedAt: new Date().toISOString(),
            selectedLanguage, // Save language
        });
    }, [jobId, interviewId, interviewFormat, questionCount, questionStep, currentQuestion, messages, isFinished, selectedLanguage]);

    const handleStartInterview = () => {
        if (!interviewId) {
            // Show paywall only for explicit limit responses (HTTP 402), not network/CORS/409 issues.
            if (isLimitReached) {
                setShowLimitModal(true);
                setError('Interview is not available on your current plan. Please upgrade to continue.');
            } else {
                setError('Interview is not initialized yet. Please wait a few seconds and try again.');
            }
            return;
        }
        setIsStarted(true);
        if (messages.length === 0) {
            fetchQuestion(interviewId);
        }
    };

    const handleFinishNow = async () => {
        if (!interviewId) return;
        try {
            setIsLoading(true);
            setError('');
            const popupStatus = buildAiProcessingStatus({
                phase: 'report',
                title: 'Building final report',
                description: 'We are compiling all answers, generating the interview summary, and preparing recommendations.',
                label: 'Final report',
                steps: [
                    { id: 'collect', label: 'Collect interview signals', status: 'active' },
                    { id: 'summary', label: 'Generate interview summary', status: 'pending' },
                    { id: 'report', label: 'Finalize final report', status: 'pending' },
                ],
            });
            showAiProcessing(popupStatus);
            const data = await runInterviewRequestWithRetry(
                () => api.post(`/api/interviews/${interviewId}/finish`),
                { attempts: 2, baseDelayMs: 1500 },
            );
            setIsFinished(true);
            setCurrentQuestion(null);
            if (data.final_report) {
                appendFinalReportMessage(data.final_report, data.structured_report);
            }
            await completeAiProcessing(
                popupStatus,
                {
                    title: 'Final report is ready',
                    description: 'The full interview summary has been added to the top of the page.',
                },
                900,
            );
        } catch (err) {
            if (err instanceof APIError) {
                if (err.status === 401 || err.status === 403) {
                    hideAiProcessing();
                    handleUnauthorized();
                    return;
                }
                if (err.status === 400) {
                    hideAiProcessing();
                    setError(formatApiError(err.data, 'Answer at least one question before finishing the interview.'));
                    return;
                }
                hideAiProcessing();
                setError(err.data?.detail || 'Failed to finish interview.');
            } else {
                hideAiProcessing();
                setError('Error finishing interview.');
            }
            console.error("Failed to finish interview", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestartInterview = async () => {
        if (!interviewId) return;

        try {
            setIsLoading(true);
            setError('');

            const data = await api.post(`/api/interviews/${interviewId}/restart`);
            questionRequestSeqRef.current += 1;

            // Clear messages and reset state to beginning
            setMessages([]);
            setQuestionStep(0);
            setCurrentQuestion(null);
            setIsFinished(false);
            setError('');

            // Fetch first question
            await fetchQuestion(interviewId);

            console.log(`[Chat] Interview restarted. ${data.questions_kept} questions kept.`);
        } catch (err) {
            if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
                handleUnauthorized();
                return;
            }
            console.error("Failed to restart interview", err);
            setError(err instanceof APIError ? (err.data?.detail || 'Failed to restart interview.') : 'Error restarting interview.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartNewInterview = async () => {
        let targetVacancyId = isUuid(vacancyIdForDetails) ? vacancyIdForDetails : null;
        let targetVacancyTitle = jobTitle;

        if (!targetVacancyId && isUuid(navigationVacancyId)) {
            targetVacancyId = navigationVacancyId;
        }

        // If user is on /chat/:interviewId route, resolve the source vacancy from interview details.
        if (!targetVacancyId && interviewId) {
            try {
                const details = await api.get(`/api/interviews/${interviewId}`);
                if (isUuid(details?.vacancy_id)) {
                    targetVacancyId = details.vacancy_id;
                }
                if (!targetVacancyTitle && typeof details?.vacancy_title === 'string') {
                    targetVacancyTitle = details.vacancy_title;
                }
            } catch (err) {
                if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
                    handleUnauthorized();
                    return;
                }
                console.error('[Chat] Failed to resolve vacancy for start-new flow:', err);
            }
        }

        if (!targetVacancyId) {
            setError('Cannot start a new interview for this vacancy. Please select the vacancy again.');
            navigate('/search');
            return;
        }

        const nextState = {
            vacancyId: targetVacancyId,
            vacancyTitle: targetVacancyTitle || undefined,
        };
        if (isManualVacancyContext) {
            nextState.source = 'manual_vacancy';
        }

        navigate(`/chat-setting/${targetVacancyId}`, { state: nextState });
    };

    const handleSendMessage = async () => {
        if (!input.trim() || isLoading || isFinished || !interviewId) return false;

        const answerText = input.trim();
        const optimisticMessage = createMessage('user', answerText);
        appendMessage(optimisticMessage);
        setInput('');
        setIsLoading(true);
        setError('');

        try {
            const answerPopupStatus = buildAiProcessingStatus({
                phase: 'analysis',
                title: 'Processing your answer',
                description: 'We are reviewing the answer, updating competency scores, and preparing the next step.',
                label: 'Answer analysis',
                steps: [
                    { id: 'analyze', label: 'Analyze answer quality', status: 'active' },
                    { id: 'bars', label: 'Update BARS scales', status: 'pending' },
                    { id: 'next', label: 'Prepare next step', status: 'pending' },
                ],
            });
            showAiProcessing(answerPopupStatus);
            const stepToSend = currentQuestion?.step ?? questionStep;

            const answerPayload = {
                answer_text: answerText,
                step: stepToSend,
            };

            const data = await runInterviewRequestWithRetry(
                () => api.post(`/api/interviews/${interviewId}/answer`, answerPayload),
            );

            // 1. Show summary (feedback)
            if (data.summary) {
                const normalizedFeedback = normalizeFeedbackPayload(data.summary, data.bars_evaluation);
                appendMessage(
                    createMessage(
                        'assistant',
                        normalizedFeedback.summary || data.summary,
                        'summary',
                        { bars: normalizedFeedback.bars },
                    ),
                );
            }

            // 2. Fetch NEXT question
            if (!data.is_finished) {
                await fetchQuestion(interviewId, {
                    state: 'active',
                    phase: 'question',
                    title: 'Preparing next question',
                    description: 'The interview agent is using your answer to choose the strongest next prompt.',
                    label: 'Question generation',
                    steps: [
                        { id: 'analyze', label: 'Analyze answer quality', status: 'completed' },
                        { id: 'bars', label: 'Update BARS scales', status: 'completed' },
                        { id: 'next', label: 'Prepare next question', status: 'active' },
                    ],
                });
            } else {
                setIsFinished(true);
                setCurrentQuestion(null);
                if (data.final_report) {
                    appendFinalReportMessage(data.final_report, data.structured_report);
                }
                await completeAiProcessing(
                    answerPopupStatus,
                    {
                        title: 'Final summary is ready',
                        description: 'The interview is complete and the final report has been added to the top of the page.',
                    },
                    900,
                );
            }
            return true;
        } catch (err) {
            setMessages((prev) => prev.filter((message) => message.id !== optimisticMessage.id));
            setInput(answerText);
            if (err instanceof APIError) {
                if (err.status === 401 || err.status === 403) {
                    hideAiProcessing();
                    handleUnauthorized();
                    return false;
                }
                if (err.status === 400 || err.status === 404) {
                    // Interview finished
                    await handleFinishNow();
                    return true;
                }
                hideAiProcessing();
                setError(formatApiError(err.data, 'Failed to send the answer.'));
                return false;
            }
            console.error('[Chat] Answer send error:', err);
            hideAiProcessing();
            setError('Error sending the answer.');
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        state: {
            messages,
            input,
            isLoading,
            isBootstrapping,
            isFinished,
            interviewId,
            interviewFormat,
            questionCount,
            questionStep,
            currentQuestion,
            error,
            jobTitle,
            companyName,
            activeTab,
            vacancyIdForDetails,
            vacancyDescription,
            isStarted,
            selectedLanguage,
            welcomeMessage,
            showLangMenu,
            showLimitModal,
            limitErrorMessage,
            isLimitReached,
            aiProcessingStatus,
            messagesEndRef,
            textareaRef,
            isManualVacancyContext,
            navigationVacancyId,
        },
        actions: {
            setInput,
            setActiveTab,
            setShowLangMenu,
            setShowLimitModal,
            handleUnauthorized,
            appendMessage,
            handleStartInterview,
            handleFinishNow,
            handleRestartInterview,
            handleStartNewInterview,
            handleSendMessage,
            handleLanguageChange,
            hideAiProcessing,
        }
    };
}
